/**
 * DetailedShell — the full navigational app: the AppShell frame + the registry-driven,
 * module-gated route table. Mounted for `/*` and for `/` when the user's
 * `settings.uiMode` is `detailed`; otherwise `/` renders the simplified landing.
 */
import React, { Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { Button } from './ui/Button.js';
import { SkeletonList } from './ui/Skeleton.js';
import { AppShell } from './shell/AppShell.js';
import { ErrorBoundary } from './ErrorBoundary.js';
import { NotFound } from '../pages/NotFound.js';
import { useEnabledModules } from '../modules/useEnabledModules.js';
import { clientModuleRegistry } from '../modules/registry.js';

const ShellLoading = () => (
  <div className="mx-auto w-full max-w-md py-24">
    <SkeletonList count={3} label="Loading" />
  </div>
);

// Hook must live in a child component so it runs after QueryClientProvider mounts.
const ShellError: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center gap-3 py-24 text-center">
    <p className="text-sm text-heading">Could not load your enabled modules.</p>
    <p className="max-w-md text-xs text-muted">
      The app cannot build its navigation without them. This is usually a transient network or session problem rather
      than a missing permission.
    </p>
    <Button onClick={onRetry}>Retry</Button>
  </div>
);

const AppRoutes: React.FC = () => {
  const { data: enabledManifests, isLoading: modulesLoading, isError, refetch } = useEnabledModules();

  // QG-CLI-PAGES-001: the guard used to be `modulesLoading || !enabledManifests`, with no
  // error branch. When GET /api/me/modules fails, isLoading goes false but data stays
  // undefined — so the condition held forever and the whole app sat on "Loading..." with no
  // way out and nothing said. A failed fetch and a slow fetch must not look identical.
  if (isError) return <ShellError onRetry={() => void refetch()} />;
  if (modulesLoading || !enabledManifests) return <ShellLoading />;

  const enabledIds = new Set(enabledManifests.map((m) => m.id));
  const allRoutes = clientModuleRegistry.filter(enabledIds).flatMap((m) => m.routes);

  return (
    <Routes>
      {allRoutes.map((route, i) => (
        <Route key={route.path ?? `route-${i}`} path={route.path} element={route.element} />
      ))}
      {/*
        QG-CLI-PAGES-002: catch-all, declared LAST so it only matches when nothing else did.
        Without it an unmatched URL rendered the Layout chrome around an empty area, which is
        indistinguishable from a page that failed to load. Note this also catches routes that
        exist but belong to a module the org has not enabled — NotFound says so.
      */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export const DetailedShell: React.FC = () => (
  <AppShell>
    <Suspense fallback={<ShellLoading />}>
      <ErrorBoundary>
        <AppRoutes />
      </ErrorBoundary>
    </Suspense>
  </AppShell>
);
